import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import type { CaptureKind } from '@kapture/types';

/**
 * `localStorage`, or nothing at all.
 *
 * Private modes and locked-down webviews throw on access rather than
 * returning null, and a capture store that cannot be constructed takes the
 * whole capture flow with it. Losing the remembered subject is a small,
 * visible cost — the chip is simply absent and the inspector re-scans — so
 * unavailable storage degrades to in-memory rather than failing.
 */
function safeStorage() {
  try {
    if (typeof localStorage === 'undefined') return null;
    // Touch it: some environments expose the object and throw on use.
    const probe = '__kapture_probe__';
    localStorage.setItem(probe, '1');
    localStorage.removeItem(probe);
    return localStorage;
  } catch {
    return null;
  }
}

interface CaptureSessionState {
  /** The session the user is actively capturing into. */
  activeSessionId: string | null;
  /** The capture mode selected on the radial picker. */
  mode: CaptureKind | null;
  /** Flash/grid/HDR style toggles for the HUD. */
  hudFlags: { flash: boolean; grid: boolean; gpsTagging: boolean };
  /**
   * The form field that sent the user to a capture screen, if one did.
   *
   * A `photos` or `voice` field answers itself with captures, so it launches
   * the ordinary capture route and needs the resulting capture stamped with
   * its id. That intent lives here rather than in a search param because it
   * belongs to the capture in progress — the same thing `mode` and
   * `activeSessionId` already describe — and because the capture routes are
   * reached from the dial too, where there is no field and no param to carry.
   *
   * Cleared by the capture route once it has used it: a stale value would
   * silently attach the next dial-started photo to a field nobody chose.
   */
  linkedFieldId: string | null;
  /**
   * What the inspector is currently standing in front of — the last code
   * scanned or typed as a subject, stamped onto every capture until it
   * changes. Null means site-level, which is the correct default and the only
   * state a single-subject session ever has.
   *
   * Persisted, unlike `linkedFieldId`. A school walk runs for an hour with the
   * screen locking between doors, and an app the OS reclaims mid-round would
   * otherwise come back with no subject and silently file the rest of the
   * morning under nothing. That is the failure this whole field exists to
   * prevent, so it must survive a restart.
   */
  currentSubject: string | null;
  /**
   * Perceptual hashes of the last few frames shot in this run, newest first.
   *
   * Only for spotting the shot an inspector has just taken twice — two
   * photographs of the same plate, seconds apart, which is how a session ends
   * up padded with evidence that shows nothing new. Bounded and deliberately
   * *not* persisted: the duplicate worth catching is the one from a minute ago,
   * and a hash surviving a restart would start warning about a door
   * photographed yesterday.
   *
   * Not a column on the capture. Storing it would make it a property of the
   * evidence rather than a hint on the way in, and it would need a schema on
   * both sides for something whose whole life is thirty seconds long.
   */
  recentHashes: string[];
  setActiveSession(id: string | null): void;
  setMode(mode: CaptureKind | null): void;
  setLinkedField(fieldId: string | null): void;
  setCurrentSubject(ref: string | null): void;
  rememberHash(hash: string): void;
  toggleHudFlag(flag: keyof CaptureSessionState['hudFlags']): void;
}

/**
 * Swallows everything, so an unavailable store is simply not remembered.
 *
 * Declared above the store and not below it. `createJSONStorage` calls its
 * getter eagerly, during this module's evaluation — so with the declaration
 * underneath, the `?? noopStorage` branch read a `const` still in its temporal
 * dead zone and threw. Zustand catches that and hands the middleware no
 * storage at all, which is not the same thing: hydration is skipped instead of
 * no-oped, and every subject change logs a warning about unavailable storage.
 * The fallback this file documents had never once run.
 */
const noopStorage: Storage = {
  length: 0,
  clear() {},
  getItem: () => null,
  key: () => null,
  removeItem() {},
  setItem() {},
};

export const useCaptureSessionStore = create<CaptureSessionState>()(
  persist(
    (set) => ({
      activeSessionId: null,
      mode: null,
      hudFlags: { flash: false, grid: true, gpsTagging: true },
      linkedFieldId: null,
      recentHashes: [],
      currentSubject: null,
      setActiveSession(id) {
        // A subject belongs to the walk it was scanned on. Carrying one into
        // the next session would file a different building's evidence under
        // this building's door.
        set((state) =>
          state.activeSessionId === id
            ? { activeSessionId: id }
            : { activeSessionId: id, currentSubject: null },
        );
      },
      setMode(mode) {
        set({ mode });
      },
      rememberHash(hash) {
        if (!hash) return;
        // Six is about a minute of shooting. Long enough to catch the retake
        // an inspector forgot they took, short enough that a genuinely
        // different subject never collides with something off-screen.
        set((state) => ({ recentHashes: [hash, ...state.recentHashes].slice(0, 6) }));
      },
      setLinkedField(fieldId) {
        set({ linkedFieldId: fieldId });
      },
      setCurrentSubject(ref) {
        set({ currentSubject: ref });
      },
      toggleHudFlag(flag) {
        set((state) => ({ hudFlags: { ...state.hudFlags, [flag]: !state.hudFlags[flag] } }));
      },
    }),
    {
      name: 'kapture.capture-session',
      storage: createJSONStorage(() => safeStorage() ?? noopStorage),
      // Only what has to outlive a restart. `linkedFieldId` is deliberately
      // transient — the capture route clears it once used, and a stale one
      // would attach the next dial-started photo to a field nobody chose.
      partialize: (state) => ({
        activeSessionId: state.activeSessionId,
        currentSubject: state.currentSubject,
      }),
    },
  ),
);
